import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { Modal } from '@/components/ui/Modal';
import { accountApi, type AccountProfile } from '@/features/account/account.api';
import { appearanceApi } from './appearance.api';
import { appearanceError, type AccountAppearance as Appearance } from './avatar';
import './AvatarEditor.css';
const AvatarEditor = lazy(() => import('./AvatarEditor'));
const PhotoEditor = lazy(() => import('./PhotoEditor'));

export const AccountAppearance = ({
  profile,
  onSaved,
}: {
  profile: AccountProfile;
  onSaved: (profile: AccountProfile) => void;
}) => {
  const [editor, setEditor] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const active = useRef(true);
  const removing = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const cache = useQueryClient();
  const key = ['account-appearance', profile.id];
  const appearance = useQuery({
    queryKey: key,
    queryFn: appearanceApi.read,
    /* Chargee pour les deux portees. Elle etait reservee aux consultants :
       un collaborateur interne n'avait nulle part ou ranger une photo, les
       colonnes n'existant que sur `candidates`. */
    refetchOnWindowFocus: false,
  });
  /**
   * Répercute la photo sur la session, et donc sur l'en-tête.
   *
   * ── Pourquoi `onSaved` et pas seulement le cache ─────────────────────────
   * `setQueryData` ne met à jour que cette page. Le menu de l'en-tête lit
   * l'utilisateur de la session, pas cette requête : sans `onSaved`, on
   * déposait une photo et l'en-tête gardait l'ancienne image jusqu'au
   * rechargement. C'est ce que faisait déjà l'éditeur d'avatar ; la photo
   * l'avait oublié.
   *
   * L'arbitrage reproduit celui du serveur (`userImageUrl`) : la photo
   * l'emporte, et retomber sur l'avatar quand elle est retirée.
   */
  const applyAppearance = (data: Appearance, message: string) => {
    cache.setQueryData(key, data);
    onSaved({ ...profile, avatarUrl: data.photoUrl ?? data.avatarUrl });
    setNotice(message);
  };
  const savedPhoto = (data: Appearance) => {
    applyAppearance(data, 'Votre photo a été enregistrée.');
  };
  const remove = async () => {
    if (!appearance.data || removing.current) return;
    removing.current = true;
    setPending(true);
    setError('');
    try {
      const data = await appearanceApi.removePhoto(appearance.data.photoVersion);
      if (active.current) {
        setDeleting(false);
        // L'en-tête doit retomber sur l'avatar généré, pas garder une URL morte.
        applyAppearance(data, 'Votre photo a été supprimée.');
      }
    } catch (cause) {
      if (active.current) setError(appearanceError(cause));
    } finally {
      removing.current = false;
      if (active.current) setPending(false);
    }
  };
  return (
    <div className="account-appearance">
      <section className="account-appearance__item">
        <h3>Votre avatar</h3>
        <p>Créez votre personnage et retrouvez-le dans votre menu de compte.</p>
        <Button
          variant="secondary"
          size="sm"
          fullWidth
          onClick={() => {
            setNotice('');
            setEditor(true);
          }}
        >
          {profile.avatarUrl ? 'Personnaliser mon avatar' : 'Créer mon avatar'}
        </Button>
      </section>
      <section className="account-appearance__item">
        <h3>Votre photo</h3>
        <p>
          {accountApi.isConsultant
            ? 'Une photo partagée avec votre fiche candidat, indépendante de votre avatar.'
            : 'Votre portrait, visible par votre équipe. Indépendant de votre avatar : supprimer l’un ne touche pas à l’autre.'}
        </p>
        {appearance.isPending ? (
          <p role="status">Chargement…</p>
        ) : appearance.isError ? (
          <>
            <p role="alert">{appearanceError(appearance.error)}</p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void appearance.refetch();
              }}
            >
              Réessayer
            </Button>
          </>
        ) : (
          <>
            {appearance.data?.photoUrl && (
              <div className="account-appearance__photo">
                <img src={appearance.data.photoUrl} alt="Votre photo" width="56" height="56" />
                <span className="text-xs text-ink-500">Photo du compte</span>
              </div>
            )}
            <input
              ref={input}
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  setNotice('');
                  setFile(e.target.files[0]);
                }
                e.target.value = '';
              }}
            />
            <div className="account-appearance__actions">
              <Button variant="secondary" size="sm" onClick={() => input.current?.click()}>
                {appearance.data?.photoUrl ? 'Changer la photo' : 'Ajouter une photo'}
              </Button>
              {appearance.data?.photoUrl && (
                <button
                  type="button"
                  className="account-appearance__remove"
                  onClick={() => {
                    setError('');
                    setDeleting(true);
                  }}
                >
                  Supprimer
                </button>
              )}
            </div>
          </>
        )}
      </section>
      {notice && (
        <p role="status" className="mt-3 text-xs text-emerald-700">
          {notice}
        </p>
      )}
      {editor && (
        <Suspense
          fallback={
            <Modal title="Votre atelier d’avatar" onClose={() => setEditor(false)}>
              <p role="status">Chargement…</p>
            </Modal>
          }
        >
          <AvatarEditor
            accountId={profile.id}
            onClose={() => setEditor(false)}
            onSaved={(updated) => {
              if ('id' in updated) onSaved(updated);
              void cache.invalidateQueries({ queryKey: key });
              setNotice('Votre avatar a été enregistré.');
            }}
          />
        </Suspense>
      )}
      {file && appearance.data && (
        <Suspense
          fallback={
            <Modal title="Votre photo" onClose={() => setFile(null)}>
              <p role="status">Chargement…</p>
            </Modal>
          }
        >
          <PhotoEditor
            file={file}
            version={appearance.data.photoVersion}
            onClose={() => setFile(null)}
            onSaved={savedPhoto}
          />
        </Suspense>
      )}
      {deleting && (
        <ConfirmationModal
          title="Supprimer votre photo ?"
          description="Votre avatar est conservé. Vous pourrez ajouter une autre photo à tout moment."
          pending={pending}
          error={error}
          onCancel={() => setDeleting(false)}
          onConfirm={() => {
            void remove();
          }}
        />
      )}
    </div>
  );
};
